/* tslint:disable:variable-name */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import {RouterStore} from '@paperlessio/sdk/util';
import {filter, map, switchMap, throttleTime} from 'rxjs/operators';
import {Point} from '@angular/cdk/drag-drop';
import {DragMode} from '@blocks/table/drag-mode';
import {DropdownComponent} from '@paperlessio/sdk/components/dropdown';
import {TableInteractionComponent} from '@blocks/table/table-interaction/table-interaction.component';
import {asyncScheduler, BehaviorSubject, fromEvent, Subscription} from 'rxjs';
import {BLOCKS_CONFIG, BlocksConfig} from '@blocks/blocks.config';
import {TableResizeColumnsDirective} from '@blocks/table/table-directives/table-resize-columns.directive';
import {BlockChangeDetectionService} from '@blocks/services/block-change-detection/block-change-detection.service';
import {TableAddDeleteDirective} from '@blocks/table/table-directives/table-add-delete.directive';
import {DropdownMode} from '@blocks/table/dropdown-mode';
import {CellSelection} from '@blocks/table/cell-selection';
import {TableCellSelectableDirective} from '@blocks/table/table-directives/table-cell-selectable.directive';
import {DOCUMENT} from '@angular/common';
import {Table, TableCell, Token} from '@paperlessio/sdk/api/models'
import {TokenStore} from '@management/editor/tokens/token.store';
import {BlockStore} from '@blocks/block/block.store';
import {TableService} from '@blocks/table/table.service';
import {LoaderService} from '@paperlessio/sdk/interop';
import {RichTextToolbarComponent, RichTextToolbarState} from '@blocks/rich-text/rich-text-toolbar/rich-text-toolbar.component';
import {RichTextService} from '@blocks/rich-text/rich-text.service';
import {instances} from '@blocks/rich-text/variable.blot';

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TableComponent implements OnInit, OnDestroy {
  // The minimal amount the cursor needs to move for a drag to be detected
  readonly dragThreshhold = 15;

  @ViewChild('contextDropdownComponent') contextDropdownComponent: DropdownComponent;
  @ViewChild('resizeColumns') resizeColumns: TableResizeColumnsDirective;
  @ViewChild('addDelete') addDelete: TableAddDeleteDirective;
  @ViewChild('cellSelectable') cellSelectable: TableCellSelectableDirective;
  @ViewChild('toolbarDropdown') toolbarDropdown: DropdownComponent;
  @ViewChild('toolbar') toolbar: RichTextToolbarComponent;

  @ViewChildren(TableInteractionComponent) interactionComponents: QueryList<TableInteractionComponent>;
  @ViewChildren('cellElement') cellElements: QueryList<ElementRef>;

  blockSelected$ = new BehaviorSubject<boolean>(false);

  // Used in the template to communicate the currently selected text inside of an rte
  selectedText: string;

  // Used by the toolbar dropdown to stay on screen
  scrollingContainer: HTMLDivElement;

  // The coordinates of the currently focused editor
  currentEditorFocus: Point;

  // Flag if a column or row is currently being dragged
  dragging = false;

  // This is the offset of the cursor inside the dragged interaction element
  // This Point is used to position the drag-shadow correctly behind the cursor
  dragStartOffset: Point;

  // The distance to the point where the drag has been initiated
  dragDistance: Point;

  // Tracks what is being tracked. Col or row.
  dragMode: DragMode;

  // The dropdown can be opened in two different ways. By clicking the interaction elements (col / row) or by right-clicking into a cell
  // This flag tracks the way it has been opened
  dropdownMode: DropdownMode;

  // The coordinates where row can be dropped
  dropRows: number[] = [];

  // The coordinates where col can be dropped
  dropCols: number[] = [];

  // The width or height of the currently dragged col / row. This sets the width of the drag-shadow.
  dragTargetOffset: number;

  // The start index of the dragged col / row
  dragStartIndex: number;

  // The index where the row will be dropped
  nearestDropRowIndex: number;

  // The index where the col will be dropped
  nearestDropColIndex: number;

  // These variables are set when the context menu is opened to provide the index values to the method calls in the context menu
  currentColIndex: number;
  currentRowIndex: number;

  variables: Token[] = [];
  loading = false;

  accumulatedRteState: RichTextToolbarState;

  private subs = new Subscription();
  private eventHandlerSubs = new Subscription();
  private blockIds$ = new BehaviorSubject<number[]>([]);

  constructor(
    @Optional() private routerStore: RouterStore,
    @Optional() private loaderService: LoaderService,
    @Optional() private tokenStore: TokenStore,
    @Optional() private blockChangeDetectionService: BlockChangeDetectionService,
    @Optional() private blockStore: BlockStore,
    @Optional() private richTextService: RichTextService,
    @Optional() public tableService: TableService,
    @Optional() @Inject(BLOCKS_CONFIG) public blocksConfig: BlocksConfig,
    @Inject(DOCUMENT) private document: Document,
    private elementRef: ElementRef,
    public cdr: ChangeDetectorRef) {
  }

  private _cellSelection: CellSelection;

  get cellSelection(): CellSelection {
    return this._cellSelection;
  }

  set cellSelection(value: CellSelection) {
    this._cellSelection = value;
    this.tableService.currentSelection$.next(value);
  }

  private _block: Table;

  get block(): Table {
    return this._block;
  }

  @Input()
  set block(v: Table) {
    this._block = v;

    this.blockIds$.next([
      v.id,
      v.children.map(row => row.id),
      ...(v.children.map(row => row.children.map(cell => cell.id)) as any)
    ].reduce((acc, curr) => {
      if (Array.isArray(curr)) {
        return [...acc, ...curr];
      }

      return [...acc, curr];
    }, []));
  }

  get selectedRichTextIds(): number[] {
    if (!this.cellSelection) {
      return [];
    }

    return this.children
      .filter((v, index) => index >= this.cellSelection.y1 && index <= this.cellSelection.y2)
      .map(row => row.children
        .filter((v, index) => index >= this.cellSelection.x1 && index <= this.cellSelection.x2)
        .map(c => c.children[0].id)).reduce((acc, curr) => [...acc, ...curr], []);
  }

  get children() {
    return this.block.children;
  }

  get blockSelected(): boolean {
    return this.blockSelected$.value;
  }

  set blockSelected(v: boolean) {
    this.blockSelected$.next(v);
  }

  private cellSelectionSub = new Subscription();

  ngOnInit() {
    this.scrollingContainer = this.document.querySelector<HTMLDivElement>('.bucket-wrapper');

    // Just to be sure we unsubscribe this thing in onDestroy:
    this.subs.add(this.cellSelectionSub);

    this.subs.add(this.blockSelected$
      .subscribe(blockSelected => {
        if (blockSelected) {
          this.cellSelectionSub = this.tableService.currentSelection$
            .pipe(filter(v => !!v))
            .subscribe(selection => {
              this.accumulatedRteState = new RichTextToolbarState({});
              this.getEditorState();

              if (selection && (selection.width >= 1 || selection.height >= 1) && this.toolbarDropdown && !this.toolbarDropdown.visible) {
                this.toolbarDropdown.show();
              }

              if (!selection && this.toolbarDropdown && this.toolbarDropdown.visible) {
                this.toolbarDropdown.hide();
              }
            });
        } else {
          this.toolbarDropdown?.hide();
          this.cellSelectionSub.unsubscribe();
        }
      }));

    if (this.loaderService) {
      this.subs.add(this.loaderService.isLoading.subscribe(l => {
        this.loading = l;
        this.cdr.markForCheck();
      }));
    }

    if (this.blockChangeDetectionService) {
      this.subs.add(this.blockIds$
        .pipe(
          filter(blockIds => blockIds?.length > 0),
          switchMap(blockIds => {
            return this.blockChangeDetectionService
              .changedBlockIds$
              .pipe(filter(id => blockIds.indexOf(id) >= 0));
          }),
          throttleTime(500, asyncScheduler, {
            leading: true,
            trailing: true
          })
        )
        .subscribe(x => {
          this.cdr.markForCheck();
        }));
    }

    if (this.richTextService) {
      // Format start is emitted before any formatting-operation is executed in the rte
      this.subs.add(this.richTextService.formatStart$
        .subscribe(blockIds => {
          this.accumulatedRteState = new RichTextToolbarState({});
        }));
    }

    if (!this.routerStore || !this.blocksConfig?.editable) {
      return;
    }

    this.subs.add(this.routerStore.routeParams
      .pipe(map(params => params.block))
      .subscribe(blockId => {
        this.blockSelected = this.block.id === +blockId;

        if (this.blockSelected) {
          this.tableService.selectedTableBlock$.next(this.block);
          this.addEventHandlers();
        } else {
          // When the user has multiple tables in the document, this would prevent opening the toolbar because it's being set to null
          // after the user selects another table block which tries to set it's cell selection
          // this.cellSelection = null;
          this.removeEventHandlers();
        }

        this.cdr.detectChanges();
      }));

    this.subs.add(this.tokenStore.tokens
      .pipe(filter(value => !!value))
      .subscribe(variables => {
        this.variables = variables.sort((x, y) => x.name.localeCompare(y.name));
      }));

    this.subs.add(this.tableService.keyDown$
      .pipe(filter(e => !!e && this.blockSelected))
      .subscribe(event => {
        if (!this.cellSelection?.isSingleCellSelection && event.metaKey) {
          // Bold
          if (event.key === 'b') {
            this.toolbar.format('bold', !this.toolbar.state?.bold);
          }

          // Italic
          if (event.key === 'i') {
            this.toolbar.format('italic', !this.toolbar.state?.italic);
          }

          // Underline
          if (event.key === 'u') {
            this.toolbar.format('underline', !this.toolbar.state?.underline);
          }
        }
      }));
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  getEditorState() {
    const blocks = this.blockStore.getBlocksSync(this.selectedRichTextIds);

    for (const block of blocks) {
      const quill = Array.from(instances.entries()).find(kv => kv[1].block.id === block.id)[0];

      if (quill) {
        this.addRteState(RichTextToolbarState.fromQuillFormat(quill.getFormat({
          index: 0,
          length: Number.MAX_SAFE_INTEGER
        })));
      }
    }
  }

  addRteState(newState: RichTextToolbarState) {
    if (!this.accumulatedRteState) {
      this.accumulatedRteState = new RichTextToolbarState({});
    }

    if (!newState) {
      newState = new RichTextToolbarState({});
    }

    this.accumulatedRteState = new RichTextToolbarState({
      bold: this.accumulatedRteState.bold || newState.bold,
      italic: this.accumulatedRteState.italic || newState.italic,
      strike: this.accumulatedRteState.strike || newState.strike,
      underline: this.accumulatedRteState.underline || newState.underline,
      blockquote: this.accumulatedRteState.blockquote || newState.blockquote,
      align: this.accumulatedRteState.align ?? newState.align,
      background: this.accumulatedRteState.background ?? newState.background,
      color: this.accumulatedRteState.color ?? newState.color,
      font: this.accumulatedRteState.font ?? newState.font,
      header: this.accumulatedRteState.header ?? newState.header,
      indent: this.accumulatedRteState.indent ?? newState.indent,
      link: this.accumulatedRteState.link ?? newState.link,
      list: this.accumulatedRteState.list ?? newState.list,
      script: this.accumulatedRteState.script ?? newState.script,
      size: this.accumulatedRteState.size ?? newState.size
    });

    this.cdr.markForCheck();
  }

  getSelectedChildren(): TableCell[][] {
    return this.cellSelection?.getSelectedChildren(this.block) as TableCell[][];
  }

  getColumnWidth(colIndex: number) {
    if (this.resizeColumns?.primaryNewWidth != null && colIndex === this.resizeColumns.colToResize) {
      return this.resizeColumns.primaryNewWidth;
    } else if (this.resizeColumns?.secondaryNewWidth != null && colIndex === this.resizeColumns.colToResize + 1) {
      return this.resizeColumns.secondaryNewWidth;
    } else {
      // Get width of the cell in the first row. always
      return this.block.settings.colWidths[colIndex];
    }
  }

  getRowCount(): number {
    return this.children.length;
  }

  openContextMenu(event: MouseEvent, dropdownMode: DropdownMode) {
    if (!this.blockSelected) {
      return;
    }

    if (this.contextDropdownComponent.visible) {
      this.contextDropdownComponent.hide();
    }

    this.currentColIndex = +(event.target as HTMLElement).closest('td').dataset.colindex;
    this.currentRowIndex = +(event.target as HTMLElement).closest('td').dataset.rowindex;

    event.preventDefault();
    event.stopPropagation();
    this.dropdownMode = dropdownMode;
    this.contextDropdownComponent.dropdownPosition = {
      x: event.clientX,
      y: event.clientY
    };
    this.contextDropdownComponent.toggle();
    this.cellSelection = null;
  }

  handleRteKeyUp(event: KeyboardEvent) {
    if (this.cellSelection && this.cellSelection.isSingleCellSelection && this.currentEditorFocus) {
      this.tableService.keyUp$.next(event);
    }
  }

  handleRteKeyDown(event: KeyboardEvent) {
    if (this.cellSelection && this.cellSelection.isSingleCellSelection && this.currentEditorFocus) {
      this.tableService.keyDown$.next(event);
    }
  }

  editorFocused(colIndex: number, rowIndex: number) {
    this.currentEditorFocus = {
      x: colIndex,
      y: rowIndex
    };
  }

  editorBlurred(colIndex: number, rowIdex: number) {
    this.currentEditorFocus = null;
  }

  calculateDropLines(mode: DragMode): number[] {
    return this.interactionComponents
      .filter(tic => tic.mode === mode)
      .map(tic => tic.offset())
      .reduce((accumulator: number[], currentValue: number) => {
        return [
          ...accumulator, accumulator[accumulator.length - 1] + currentValue
        ];
      }, [0])
      .map((offset: number, index: number) => offset + (this.block.settings.borderWidth - 1) * index);
  }

  fixColSizes(tableWidth?: number) {
    if (!tableWidth) {
      tableWidth = (this.elementRef.nativeElement as HTMLElement).offsetWidth;
    }

    this.cellElements
      .map(cellElement => (cellElement.nativeElement as HTMLElement))
      .filter(cellElement => +cellElement.dataset.rowindex === 0)
      .forEach(cellElement => {
        const colindex = +cellElement.dataset.colindex;
        this.block.settings.colWidths[colindex] = cellElement.offsetWidth / tableWidth * 100;
      });

    return this.blockStore.updateBlock(this.block);
  }

  private addEventHandlers() {
    this.eventHandlerSubs
      .add(fromEvent<MouseEvent>(this.document, 'mousemove')
        .pipe(throttleTime(16))
        .subscribe(e => this.tableService.mouseMove$.next(e)));

    this.eventHandlerSubs
      .add(fromEvent<MouseEvent>(this.document, 'mouseup')
        .subscribe(e => this.tableService.mouseUp$.next(e)));

    this.eventHandlerSubs
      .add(fromEvent<KeyboardEvent>(this.document, 'keydown')
        .subscribe(e => this.tableService.keyDown$.next(e)));

    this.eventHandlerSubs
      .add(fromEvent<KeyboardEvent>(this.document, 'keyup')
        .subscribe(e => this.tableService.keyUp$.next(e)));
  }

  private removeEventHandlers() {
    this.eventHandlerSubs.unsubscribe();
    this.eventHandlerSubs = new Subscription();
  }
}
